import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ArrowRight, Play } from "lucide-react";
import HeroDashboardMockup from "./HeroDashboardMockup";

export default function HeroSection() {
  return (
    <section id="hero" className="relative pt-28 pb-20 md:pt-36 md:pb-28 overflow-hidden">
      {/* Dot pattern background */}
      <div className="absolute inset-0 hero-dot-pattern pointer-events-none" />

      {/* Subtle gradient orbs */}
      <div className="absolute top-20 -left-40 w-96 h-96 bg-orange-200/20 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute top-40 -right-40 w-96 h-96 bg-fuchsia-200/20 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-[1200px] mx-auto px-6 relative z-10">
        <div className="text-center max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-orange-50 border border-orange-100 mb-6">
              <div className="w-2 h-2 rounded-full bg-orange-500 animate-pulse" />
              <span className="text-xs font-medium text-orange-600" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                Neuroscience-powered ad intelligence
              </span>
            </div>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight"
            style={{ fontFamily: 'Outfit, sans-serif', color: '#0F172A' }}
          >
            Predict Ad Performance{" "}
            <span className="gradient-text">Before You Spend</span> a Dollar
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mt-6 text-base md:text-lg leading-relaxed max-w-2xl mx-auto"
            style={{ color: '#475569' }}
          >
            Upload your ad. Get an instant neuroscience-powered analysis with attention heatmaps,
            brand visibility scores, engagement predictions, and actionable optimization recommendations.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <a href="#pricing">
              <Button
                data-testid="cta-hero-primary"
                className="gradient-btn rounded-xl h-12 px-8 text-base font-semibold"
              >
                <span className="flex items-center gap-2">
                  Start Free Analysis
                  <ArrowRight size={18} />
                </span>
              </Button>
            </a>
            <a href="#how-it-works">
              <Button
                data-testid="cta-hero-secondary"
                variant="outline"
                className="rounded-xl h-12 px-8 text-base font-medium border-slate-300 hover:border-orange-300 hover:bg-orange-50/50 text-slate-700"
              >
                <Play size={16} className="mr-2 text-orange-500" />
                See How It Works
              </Button>
            </a>
          </motion.div>

          {/* Trust badges */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="mt-10 flex flex-wrap items-center justify-center gap-6 text-xs font-medium"
            style={{ color: '#94A3B8' }}
          >
            <div className="flex items-center gap-2">
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
              Powered by neuroscience research
            </div>
            <div className="flex items-center gap-2">
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
              Built on 30+ years of research
            </div>
            <div className="flex items-center gap-2">
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
              Dual-AI architecture
            </div>
          </motion.div>
        </div>

        {/* Hero Dashboard Mockup */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="mt-16 max-w-4xl mx-auto"
        >
          <div className="gradient-border">
            <div className="gradient-border-inner">
              <HeroDashboardMockup />
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
